// ---------------------------------------------------------------------------
// trust.content.ts — Translated content for TrustBadgesSection
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';

export interface TrustContent {
  badge: string;
  title: string;
  subtitle: string;
  badges: Array<{ label: string; description: string }>;
  logos: Array<{ initials: string; name: string; location: string }>;
}

const en: TrustContent = {
  badge: 'Trusted Platform',
  title: 'Trusted by 500+ Hotels Worldwide',
  subtitle: 'Enterprise-grade security and reliability powering hospitality leaders globally',
  badges: [
    { label: 'SOC 2 Type II', description: 'Certified security controls' },
    { label: 'PCI DSS Level 1', description: 'Payment security standard' },
    { label: 'GDPR Compliant', description: 'Data protection certified' },
    { label: 'ISO 27001', description: 'Information security certified' },
    { label: '99.9% Uptime SLA', description: 'Enterprise SLA guarantee' },
    { label: '256-bit AES', description: 'End-to-end encryption' },
  ],
  logos: [
    { initials: 'GI', name: 'The Greenwich Inn', location: 'London, UK' },
    { initials: 'MB', name: 'Marina Bay Suites', location: 'Dubai, UAE' },
    { initials: 'SH', name: 'Siam Heritage', location: 'Bangkok, Thailand' },
    { initials: 'PR', name: 'Pacific Resort Group', location: 'Sydney, Australia' },
    { initials: 'ML', name: 'Manhattan Loft Hotel', location: 'New York, US' },
    { initials: 'CS', name: 'Casa del Sol', location: 'Barcelona, Spain' },
    { initials: 'GP', name: 'The Grand Palace', location: 'Mumbai, India' },
    { initials: 'TA', name: 'Tokyo Azabu Hotel', location: 'Tokyo, Japan' },
  ],
};

const ar: TrustContent = {
  badge: 'منصة موثوقة',
  title: 'موثوق به من قبل أكثر من ٥٠٠ فندق حول العالم',
  subtitle: 'أمان وموثوقية على مستوى المؤسسات تدعم قادة الضيافة عالمياً',
  badges: [
    { label: 'SOC 2 Type II', description: 'ضوابط أمان معتمدة' },
    { label: 'PCI DSS Level ١', description: 'معيار أمان المدفوعات' },
    { label: 'GDPR', description: 'حماية البيانات معتمدة' },
    { label: 'ISO 27001', description: 'أمن المعلومات معتمد' },
    { label: '٩٩٫٩% SLA', description: 'ضمان وقت التشغيل' },
    { label: '٢٥٦-bit AES', description: 'تشفير شامل' },
  ],
  logos: [
    { initials: 'GI', name: 'ذا غرينتش إن', location: 'لندن، المملكة المتحدة' },
    { initials: 'MB', name: 'مارينا باي سويتس', location: 'دبي، الإمارات' },
    { initials: 'SH', name: 'سيام هيريتاج', location: 'بانكوك، تايلاند' },
    { initials: 'PR', name: 'باسيفيك ريزورت غروب', location: 'سيدني، أستراليا' },
    { initials: 'ML', name: 'مانهاتن لوفت هوتيل', location: 'نيويورك، أمريكا' },
    { initials: 'CS', name: 'كاسا ديل سول', location: 'برشلونة، إسبانيا' },
    { initials: 'GP', name: 'ذا غراند بالاس', location: 'مومباي، الهند' },
    { initials: 'TA', name: 'طوكيو أزابو هوتيل', location: 'طوكيو، اليابان' },
  ],
};

const th: TrustContent = {
  badge: 'แพลตฟอร์มที่เชื่อถือได้',
  title: 'ได้รับความไว้วางใจจากโรงแรมกว่า 500 แห่งทั่วโลก',
  subtitle: 'ความปลอดภัยและความน่าเชื่อถือระดับองค์กรสำหรับผู้นำด้านการโรงแรมทั่วโลก',
  badges: [
    { label: 'SOC 2 Type II', description: 'การควบคุมความปลอดภัยที่ได้รับการรับรอง' },
    { label: 'PCI DSS Level 1', description: 'มาตรฐานความปลอดภัยการชำระเงิน' },
    { label: 'GDPR', description: 'การปกป้องข้อมูลที่ได้รับการรับรอง' },
    { label: 'ISO 27001', description: 'ความปลอดภัยข้อมูลที่ได้รับการรับรอง' },
    { label: '99.9% SLA', description: 'การรับประกันความพร้อมใช้งาน' },
    { label: '256-bit AES', description: 'การเข้ารหัสแบบครบวงจร' },
  ],
  logos: [
    { initials: 'GI', name: 'เดอะ กรีนิช อินน์', location: 'ลอนดอน, สหราชอาณาจักร' },
    { initials: 'MB', name: 'มารีน่า เบย์ สวีท', location: 'ดูไบ, สหรัฐอาหรับเอมิเรตส์' },
    { initials: 'SH', name: 'สยาม เฮอริเทจ', location: 'กรุงเทพฯ, ไทย' },
    { initials: 'PR', name: 'แปซิฟิก รีสอร์ท กรุ๊ป', location: 'ซิดนีย์, ออสเตรเลีย' },
    { initials: 'ML', name: 'แมนฮัตตัน ลอฟท์ โฮเทล', location: 'นิวยอร์ก, สหรัฐอเมริกา' },
    { initials: 'CS', name: 'คาซา เดล โซล', location: 'บาร์เซโลนา, สเปน' },
    { initials: 'GP', name: 'เดอะ แกรนด์ พาเลซ', location: 'มุมไบ, อินเดีย' },
    { initials: 'TA', name: 'โตเกียว อาซาบุ โฮเทล', location: 'โตเกียว, ญี่ปุ่น' },
  ],
};

const es: TrustContent = {
  badge: 'Plataforma de Confianza',
  title: 'Más de 500 Hoteles Confían en Nosotros',
  subtitle: 'Seguridad y fiabilidad de nivel empresarial para líderes hoteleros a nivel mundial',
  badges: [
    { label: 'SOC 2 Type II', description: 'Controles de seguridad certificados' },
    { label: 'PCI DSS Level 1', description: 'Estándar de seguridad de pagos' },
    { label: 'GDPR', description: 'Protección de datos certificada' },
    { label: 'ISO 27001', description: 'Seguridad de información certificada' },
    { label: '99.9% SLA', description: 'Garantía de disponibilidad' },
    { label: '256-bit AES', description: 'Cifrado de extremo a extremo' },
  ],
  logos: [
    { initials: 'GI', name: 'The Greenwich Inn', location: 'Londres, Reino Unido' },
    { initials: 'MB', name: 'Marina Bay Suites', location: 'Dubái, EAU' },
    { initials: 'SH', name: 'Siam Heritage', location: 'Bangkok, Tailandia' },
    { initials: 'PR', name: 'Pacific Resort Group', location: 'Sídney, Australia' },
    { initials: 'ML', name: 'Manhattan Loft Hotel', location: 'Nueva York, EE.UU.' },
    { initials: 'CS', name: 'Casa del Sol', location: 'Barcelona, España' },
    { initials: 'GP', name: 'The Grand Palace', location: 'Bombay, India' },
    { initials: 'TA', name: 'Tokyo Azabu Hotel', location: 'Tokio, Japón' },
  ],
};

const fr: TrustContent = {
  badge: 'Plateforme de Confiance',
  title: 'Plus de 500 Hôtels Nous Font Confiance',
  subtitle: 'Sécurité et fiabilité de niveau entreprise pour les leaders de l\'hôtellerie mondiale',
  badges: [
    { label: 'SOC 2 Type II', description: 'Contrôles de sécurité certifiés' },
    { label: 'PCI DSS Level 1', description: 'Norme de sécurité des paiements' },
    { label: 'GDPR', description: 'Protection des données certifiée' },
    { label: 'ISO 27001', description: 'Sécurité de l\'information certifiée' },
    { label: '99.9% SLA', description: 'Garantie de disponibilité' },
    { label: '256-bit AES', description: 'Chiffrement de bout en bout' },
  ],
  logos: [
    { initials: 'GI', name: 'The Greenwich Inn', location: 'Londres, Royaume-Uni' },
    { initials: 'MB', name: 'Marina Bay Suites', location: 'Dubaï, EAU' },
    { initials: 'SH', name: 'Siam Heritage', location: 'Bangkok, Thaïlande' },
    { initials: 'PR', name: 'Pacific Resort Group', location: 'Sydney, Australie' },
    { initials: 'ML', name: 'Manhattan Loft Hotel', location: 'New York, États-Unis' },
    { initials: 'CS', name: 'Casa del Sol', location: 'Barcelone, Espagne' },
    { initials: 'GP', name: 'The Grand Palace', location: 'Mumbai, Inde' },
    { initials: 'TA', name: 'Tokyo Azabu Hotel', location: 'Tokyo, Japon' },
  ],
};

const de: TrustContent = {
  badge: 'Vertrauenswürdige Plattform',
  title: 'Über 500 Hotels Weltweit Vertrauen Uns',
  subtitle: 'Sicherheit und Zuverlässigkeit auf Unternehmensniveau für Hotelführer weltweit',
  badges: [
    { label: 'SOC 2 Type II', description: 'Zertifizierte Sicherheitskontrollen' },
    { label: 'PCI DSS Level 1', description: 'Zahlungssicherheitsstandard' },
    { label: 'GDPR', description: 'Datenschutz zertifiziert' },
    { label: 'ISO 27001', description: 'Informationssicherheit zertifiziert' },
    { label: '99.9% SLA', description: 'Verfügbarkeitsgarantie' },
    { label: '256-bit AES', description: 'Ende-zu-Ende-Verschlüsselung' },
  ],
  logos: [
    { initials: 'GI', name: 'The Greenwich Inn', location: 'London, Großbritannien' },
    { initials: 'MB', name: 'Marina Bay Suites', location: 'Dubai, VAE' },
    { initials: 'SH', name: 'Siam Heritage', location: 'Bangkok, Thailand' },
    { initials: 'PR', name: 'Pacific Resort Group', location: 'Sydney, Australien' },
    { initials: 'ML', name: 'Manhattan Loft Hotel', location: 'New York, USA' },
    { initials: 'CS', name: 'Casa del Sol', location: 'Barcelona, Spanien' },
    { initials: 'GP', name: 'The Grand Palace', location: 'Mumbai, Indien' },
    { initials: 'TA', name: 'Tokyo Azabu Hotel', location: 'Tokio, Japan' },
  ],
};

const ja: TrustContent = {
  badge: '信頼のプラットフォーム',
  title: '世界500以上のホテルが信頼',
  subtitle: 'エンタープライズグレードのセキュリティと信頼性で世界のホスピタリティリーダーを支援',
  badges: [
    { label: 'SOC 2 Type II', description: '認証済みセキュリティ管理' },
    { label: 'PCI DSS Level 1', description: '決済セキュリティ基準' },
    { label: 'GDPR', description: 'データ保護認証済み' },
    { label: 'ISO 27001', description: '情報セキュリティ認証済み' },
    { label: '99.9% SLA', description: '稼働率保証' },
    { label: '256-bit AES', description: 'エンドツーエンド暗号化' },
  ],
  logos: [
    { initials: 'GI', name: 'ザ・グリニッチ・イン', location: 'ロンドン、英国' },
    { initials: 'MB', name: 'マリーナベイスイーツ', location: 'ドバイ、UAE' },
    { initials: 'SH', name: 'サイアム・ヘリテージ', location: 'バンコク、タイ' },
    { initials: 'PR', name: 'パシフィック・リゾート・グループ', location: 'シドニー、豪州' },
    { initials: 'ML', name: 'マンハッタン・ロフト・ホテル', location: 'ニューヨーク、米国' },
    { initials: 'CS', name: 'カサ・デル・ソル', location: 'バルセロナ、スペイン' },
    { initials: 'GP', name: 'ザ・グランド・パレス', location: 'ムンバイ、インド' },
    { initials: 'TA', name: '東京麻布ホテル', location: '東京、日本' },
  ],
};

const zh: TrustContent = {
  badge: '值得信赖的平台',
  title: '全球500多家酒店的信赖之选',
  subtitle: '企业级安全性和可靠性，赋能全球酒店业领导者',
  badges: [
    { label: 'SOC 2 Type II', description: '认证安全控制' },
    { label: 'PCI DSS Level 1', description: '支付安全标准' },
    { label: 'GDPR', description: '数据保护认证' },
    { label: 'ISO 27001', description: '信息安全认证' },
    { label: '99.9% SLA', description: '运行时间保证' },
    { label: '256-bit AES', description: '端到端加密' },
  ],
  logos: [
    { initials: 'GI', name: '格林威治酒店', location: '伦敦，英国' },
    { initials: 'MB', name: '滨海湾套房', location: '迪拜，阿联酋' },
    { initials: 'SH', name: '暹罗遗产', location: '曼谷，泰国' },
    { initials: 'PR', name: '太平洋度假集团', location: '悉尼，澳大利亚' },
    { initials: 'ML', name: '曼哈顿阁楼酒店', location: '纽约，美国' },
    { initials: 'CS', name: '太阳之家', location: '巴塞罗那，西班牙' },
    { initials: 'GP', name: '大皇宫酒店', location: '孟买，印度' },
    { initials: 'TA', name: '东京麻布酒店', location: '东京，日本' },
  ],
};

const hi: TrustContent = {
  badge: 'विश्वसनीय प्लेटफॉर्म',
  title: 'दुनिया भर में 500+ होटलों द्वारा विश्वसनीय',
  subtitle: 'वैश्विक स्तर पर आतिथ्य नेताओं को शक्ति प्रदान करने वाली एंटरप्राइज़-ग्रेड सुरक्षा और विश्वसनीयता',
  badges: [
    { label: 'SOC 2 Type II', description: 'प्रमाणित सुरक्षा नियंत्रण' },
    { label: 'PCI DSS Level 1', description: 'भुगतान सुरक्षा मानक' },
    { label: 'GDPR अनुपालित', description: 'डेटा सुरक्षा प्रमाणित' },
    { label: 'ISO 27001', description: 'सूचना सुरक्षा प्रमाणित' },
    { label: '99.9% अपटाइम SLA', description: 'एंटरप्राइज़ SLA गारंटी' },
    { label: '256-bit AES', description: 'एंड-टू-एंड एन्क्रिप्शन' },
  ],
  logos: [
    { initials: 'GI', name: 'द ग्रीनविच इन', location: 'लंदन, यूके' },
    { initials: 'MB', name: 'मरीना बे सुइट्स', location: 'दुबई, UAE' },
    { initials: 'SH', name: 'सियाम हेरिटेज', location: 'बैंकॉक, थाईलैंड' },
    { initials: 'PR', name: 'पैसिफिक रिसॉर्ट ग्रुप', location: 'सिडनी, ऑस्ट्रेलिया' },
    { initials: 'ML', name: 'मैनहट्टन लॉफ्ट होटल', location: 'न्यूयॉर्क, USA' },
    { initials: 'CS', name: 'कासा डेल सोल', location: 'बार्सिलोना, स्पेन' },
    { initials: 'GP', name: 'द ग्रैंड पैलेस', location: 'मुंबई, भारत' },
    { initials: 'TA', name: 'टोक्यो अज़ाबु होटल', location: 'टोक्यो, जापान' },
  ],
};

export const TRUST_CONTENT: LocaleContentMap<TrustContent> = { en, ar, th, es, fr, de, ja, zh, hi };
